import { Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import PopupButton from "../common/PopupButton";
import PopupModelWrapper from "../wrappers/PopupModelWrapper";
import useData from "src/hooks/useApi";
import useMessage from "src/hooks/useMessage";
import PopupLoading from "../common/PopupLoading";
import {
    DropScheduleRecord,
    PreBackorderCheckResponse,
    PreBackorderStatus,
} from "./PreBackorderModel";
import {
    dateSelectBoxButtonStyle,
    dateSelectBoxButtonTitleStyle,
    dateSelectDomainNameStyle,
} from "./modelStyles";
import PreBackorderScheduleTable from "../table/PreBackorderScheduleTable";
import { formatDate } from "src/utils/helperFunctions";

type PreBackorderEdit = {
    open: boolean;
    domainName: string;
    bidAmount: number;
    setOpen: Dispatch<SetStateAction<boolean>>;
    clearSelectedBid: () => void;
    setReloadData: React.Dispatch<React.SetStateAction<number>>;
};

const EditPreBackorderModel = (props: PreBackorderEdit) => {
    const {
        open,
        domainName,
        bidAmount,
        setOpen,
        clearSelectedBid,
        setReloadData,
    } = props;

    const { postData } = useData();
    const { fetchData } = useData();
    const { showError, showSuccess } = useMessage();

    const [schedule, setSchedule] = useState<DropScheduleRecord[]>([]);

    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    const [isScheduleLoading, setIsScheduleLoading] = useState<boolean>(true);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const executeUpdate = async () => {
        try {
            setIsProcessing(true);
            if (selectedIndex === null) return;

            const data = schedule[selectedIndex];

            const response: any = await postData("/bid/updatePreBackorderBid", {
                domainName: domainName,
                bidAmount: data.fee,
            });

            if (response?.success) {
                showSuccess(
                    `The bid amount for ${domainName} updated successfully. Drop date will be updated shortly.`,
                );
                return;
            }

            const error = response?.message || "Bid Amount Update failed";
            showError(error);
        } catch (error: any) {
            showError(`Bid Amount update for ${domainName} domain failed`);
        } finally {
            setIsProcessing(false);
            setReloadData(Date.now() / 1000);
            handleClose();
        }
    };

    const setupDropSchedule = async () => {
        setIsScheduleLoading(true);
        try {
            const response = await fetchData(
                `/bid/getDomainPreBackorderSchedule/${domainName}`,
            );
            if (!response) {
                throw new Error("Response not received");
            }

            const { status, schedule } = response as PreBackorderCheckResponse;

            if (status === PreBackorderStatus.FETCH_FAILED) {
                throw new Error(
                    "Failed to fetch drop schedule. Please try again later",
                );
            }

            if (status === PreBackorderStatus.NOT_AVAILABLE_FOR_PRE_BACKORDER) {
                throw new Error(
                    "Pre-backordering is not allowed as the drop window has expired. Backordering is still available.",
                );
            }

            if (status !== PreBackorderStatus.AVAILABLE) {
                throw new Error(
                    "Drop Schedule not available, please try again !",
                );
            }

            // Schedule is received in the ascending order of the bid amount

            let tempSelectedIndex = -1;
            let firstValidIndex = -1;

            schedule.forEach((record, index) => {
                if (firstValidIndex === -1 && record.isValid)
                    firstValidIndex = index;
                if (record.fee === bidAmount && record.isValid)
                    tempSelectedIndex = index;
            });

            if (tempSelectedIndex > 0) {
                setSelectedIndex(tempSelectedIndex);
            } else if (firstValidIndex > 0) {
                setSelectedIndex(firstValidIndex);
            } else {
                setSelectedIndex(0);
            }

            setSchedule(schedule);
        } catch (error: any) {
            console.log(error);
            await new Promise((resolve) => setTimeout(resolve, 2000));
            showError(error.message);
            handleClose();
        } finally {
            setIsScheduleLoading(false);
        }
    };

    const handleClose = () => {
        clearSelectedBid();
        setOpen(false);
    };

    useEffect(() => {
        setupDropSchedule();
    }, []);

    if (isScheduleLoading) {
        return (
            <PopupModelWrapper
                open={open}
                handleClose={handleClose}
                title="Update Pre Backorder"
            >
                <PopupLoading />
            </PopupModelWrapper>
        );
    }

    return (
        <PopupModelWrapper
            open={open}
            handleClose={handleClose}
            width={{ xs: "80%", sm: "300px", md: "350px" }}
            title="Pre-Backorder Domain Update"
        >
            <Stack spacing={1} mt={3}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography sx={dateSelectBoxButtonTitleStyle}>
                        Domain Name :
                    </Typography>
                    <Typography sx={dateSelectDomainNameStyle}>
                        {domainName}
                    </Typography>
                </Stack>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography sx={dateSelectBoxButtonTitleStyle} width={220}>
                        Selected Pre-Backorder Date:
                    </Typography>
                    <Typography sx={dateSelectBoxButtonStyle}>
                        {selectedIndex !== null
                            ? formatDate(schedule[selectedIndex].dropTimeMS)
                            : "-"}
                    </Typography>
                </Stack>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography sx={dateSelectBoxButtonTitleStyle} width={220}>
                        Bid Value ($):
                    </Typography>
                    <Typography sx={dateSelectBoxButtonStyle}>
                        {selectedIndex !== null
                            ? schedule[selectedIndex].fee.toFixed(2)
                            : "-"}
                    </Typography>
                </Stack>
            </Stack>
            <Stack
                sx={{
                    my: 3,
                    maxHeight: "50dvh",
                    overflowY: "auto",
                }}
            >
                <PreBackorderScheduleTable
                    schedule={schedule}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                />
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
                <PopupButton triggerFunction={handleClose} type="cancel">
                    Cancel
                </PopupButton>
                <PopupButton
                    triggerFunction={executeUpdate}
                    type="proceed"
                    disabled={selectedIndex === null}
                    isLoading={isProcessing}
                >
                    Update
                </PopupButton>
            </Stack>
        </PopupModelWrapper>
    );
};

export default EditPreBackorderModel;
